// PRIMITIVA DE BUSCA COMPARTILHADA DOS HEROS (Fase M2)
//
// Extraída FIELMENTE do WelcomeSplit (o hero mais limpo). Concentra o estado da
// busca (transação/cidade/tipo/dormitórios/preço), a montagem da URL de resultados
// (mesma rota da busca padrão derivada do renderPath dos WelcomePieces), o
// `appendCodsiteQuery` e o tracking de `Search` no Meta. NÃO carrega presentation
// (cores/contraste) nem JSX — cada hero monta o próprio visual e apenas consome
// este hook. Nenhuma mudança de comportamento em relação ao WelcomeSplit original.
import { useRouter } from 'next/router';
import { hifenUrl } from '../../../utils/hifenUrl';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { CIDADES_QUERY_PARAM } from '../../../lib/region';
import { opcoesDoTexto } from '../../../utils/buscaMultiSelecao';
import { removerAcentos } from '../../../utils/FunctionsGlobals';
import { genMetaEventId, trackMetaEvent } from '../../../utils/metaPixel';
import { qtdTextoParaUrl, virgulaCrua } from '../../../utils/qtdMultiSelecao';
import { appendCodsiteQuery } from '../../../utils/url';
import { useRegiaoHref } from '../../context/Info';

export interface HeroSearchOption {
    label: string;
    value: any;
}

export interface UseHeroSearchParams {
    site: any;
    allCities?: Array<HeroSearchOption>;
    allTypes?: Array<HeroSearchOption>;
    allTransacao?: Array<{ label: string; value: string }>;
    /** repassado a `appendCodsiteQuery` (é o `codsite2` que o index.tsx entrega ao hero). */
    codsite2?: any;
    origin?: any;
}

// Escapa o hífen do segmento (a rota /imoveis usa `-` como separador de campos vazios).
export const encodeSegment = (label: unknown, codsite?: unknown): string => {
    const raw = String(label ?? '').trim();
    return raw ? hifenUrl(raw, codsite) : '';
};

export function useHeroSearch({
    site,
    allCities,
    allTypes,
    allTransacao,
    codsite2,
    origin,
}: UseHeroSearchParams) {
    const router = useRouter();
    // MULTI-FILIAIS: a busca do hero leva a filial ativa para a listagem.
    const withRegiao = useRegiaoHref();
    const transacoes = allTransacao?.length ? allTransacao : [{ label: 'Comprar', value: 'venda' }];
    const [transacao, setTransacao] = useState<string>(String(transacoes[0]?.value || 'venda').toLowerCase());
    const [cidade, setCidade] = useState('');
    const [tipo, setTipo] = useState('');
    const [dormitorios, setDormitorios] = useState('');
    // modelos novos (utils/buscaMultiSelecao): `cidade`/`tipo` podem guardar vários ids ("12,7") e o
    // Atelier escolhe bairros na barra. Nos outros heros é um id só e `bairros` fica vazio — URL de sempre.
    const [bairros, setBairros] = useState<HeroSearchOption[]>([]);
    const [precoMin, setPrecoMin] = useState('');
    const [precoMax, setPrecoMax] = useState('');
    const [loading, setLoading] = useState(false);

    const finalize = (path: string) =>
        withRegiao(
            removerAcentos(appendCodsiteQuery(path, codsite2, origin)).toLowerCase().replaceAll(' ', '-')
        );

    const buildUrl = (): string => {
        const cidades = opcoesDoTexto(allCities, cidade);
        const tipos = opcoesDoTexto(allTypes, tipo);
        // 1 cidade = no caminho, como sempre; 2+ = caminho sem cidade e `?cidades=<ids>`
        const cidadeLabel = cidades.length === 1 ? cidades[0].label : '';
        const citySegment = cidadeLabel ? encodeSegment(cidadeLabel, site?.id) : '-';
        const districtSegment = bairros.length ? bairros.map((b) => encodeSegment(b.label, site?.id)).join('+') : '-';
        const typeSegment = tipos.length ? tipos.map((t) => encodeSegment(t.label, site?.id)).join('+') : '-';

        const params = new URLSearchParams();
        const min = parseInt(precoMin.replace(/\D/g, ''), 10);
        const max = parseInt(precoMax.replace(/\D/g, ''), 10);
        if (min > 0 || max > 0) {
            params.set('min', String(min > 0 ? min : 0));
            params.set('max', String(max > 0 ? max : 0));
        }
        // modelos novos: "2,3" = vários números marcados (utils/qtdMultiSelecao); um só = o de sempre
        const dorm = qtdTextoParaUrl(dormitorios);
        if (dorm) params.set('dormitorios', dorm);
        if (cidades.length > 1) params.set(CIDADES_QUERY_PARAM, cidades.map((c) => c.value).join(','));

        const vazio = !cidades.length && !tipos.length && !bairros.length && params.toString() === '';
        let path = `/imoveis/${transacao}/${citySegment}/${districtSegment}/-/${typeSegment}`;
        if (vazio) path = `${path}?ordem=desc-inclusao`;
        else if (params.toString()) path = `${path}?${virgulaCrua(params.toString())}`;
        return finalize(path);
    };

    // Dispara o evento Search (com dedup de eventId compartilhado com o CAPI).
    const trackSearch = () => {
        const cidadeLabel = opcoesDoTexto(allCities, cidade).map((c) => c.label).join(', ');
        const tipoLabel = opcoesDoTexto(allTypes, tipo).map((t) => t.label).join(', ');
        const searchString = [transacao, tipoLabel, cidadeLabel].filter(Boolean).join(' | ');
        const eventId = genMetaEventId();
        try {
            sessionStorage.setItem('meta:searchEventId', JSON.stringify({ id: eventId, at: Date.now() }));
        } catch {
            /* sessionStorage indisponível — Search sai sem dedup c/ CAPI */
        }
        trackMetaEvent(
            site,
            'Search',
            {
                search_string: searchString,
                transaction_type: transacao,
                property_type: tipoLabel,
                city: cidadeLabel,
            },
            { eventId }
        );
    };

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (loading) return;
        setLoading(true);
        trackSearch();
        router.push(buildUrl()).catch(() => setLoading(false));
    };

    // Atalho "ver todos" da transação atual (usado por CTAs e pelo link de busca avançada).
    const verImoveisUrl = finalize(`/imoveis/${transacao}/-/-/-/-?ordem=desc-inclusao`);

    return {
        transacoes,
        transacao,
        setTransacao,
        cidade,
        setCidade,
        tipo,
        setTipo,
        dormitorios,
        setDormitorios,
        bairros,
        setBairros,
        precoMin,
        setPrecoMin,
        precoMax,
        setPrecoMax,
        loading,
        setLoading,
        buildUrl,
        finalize,
        onSubmit,
        verImoveisUrl,
    };
}
